import { useCallback, useRef, useState } from "react";
import { Invoice } from "../pages/invoice/components/InvoicePrintContent";
import { formatDate, formatTime } from "@/utils/date";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { generateCryptoUniqueOrderId } from "@/utils/uniqueId";

// Helper: Normalize Saudi numbers to E.164 format (+966...)
const toE164Ksa = (raw?: string) => {
  if (!raw) return raw as unknown as string;
  let s = String(raw).trim();
  // remove all non-digits except leading + for easier parsing
  const hasPlus = s.startsWith("+");
  s = (hasPlus ? s.slice(1) : s).replace(/\D/g, "");

  // Normalize common country code variants
  if (s.startsWith("00966")) s = s.replace(/^00966/, "966");
  let national = s;
  if (s.startsWith("966")) {
    national = s.slice(3);
  } else if (s.startsWith("05")) {
    // mobile local format -> drop leading 0
    national = s.slice(1);
  } else if (s.startsWith("0")) {
    // landline or other local starting with 0 -> drop leading 0
    national = s.slice(1);
  } else if (s.startsWith("5")) {
    national = s; // already without leading 0
  } else {
    national = s; // fallback: use as-is
  }

  // Limit to max 10 digits after +966 per UI constraint
  national = national.slice(0, 10);

  return `+966${national}`;
};

export const useCartLogic = ({
  cart,
  coupon,
  finalPrice,
  api,
  createInvoice,
  clearCart,
  closeConfirmModal,
  printRef,
  pendingPaymentData,
  showErrorPopup,
}: {
  cart: any[];
  coupon: any;
  finalPrice: number;
  api: any;
  createInvoice: any;
  clearCart: () => void;
  closeConfirmModal: () => void;
  printRef: React.RefObject<HTMLDivElement>;
  pendingPaymentData?: any;
  showErrorPopup?: (message: string) => void;
}) => {
  const [generatedInvoice, setGeneratedInvoice] = useState<Invoice | null>(
    null
  );
  const [printInvoice, setPrintInvoice] = useState<Invoice | null>(null);
  // حالة تحميل لإنشاء الفاتورة من الباك اند
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
  // وضع الطباعة: فاتورة عادية أو هدية
  const [isGiftPrint, setIsGiftPrint] = useState<boolean>(false);
  const [giftInvoiceData, setGiftInvoiceData] = useState<any | null>(null);
  // حماية من API call duplicates
  const apiCallInProgress = useRef(false);
  const navigate = useNavigate();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Invoice-${generatedInvoice?.id}`,
    onAfterPrint: () => {
      clearCart();
      toast.success("تم طباعة الفاتورة بنجاح");
      navigate("/invoices");
      setGeneratedInvoice(null);
    },
  });

  const onSubmit = useCallback(
    async (data: any) => {
      if (cart.length === 0) return;

      // حماية من المعالجة المكررة: إذا كان هناك فاتورة قيد الإنشاء، لا نبدأ عملية جديدة
      if (isCreatingInvoice || apiCallInProgress.current) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "⚠️ onSubmit blocked - invoice creation or API call in progress"
          );
        }
        return;
      }

      // إذا كان هناك خصم 100%، نستخدم مسار الفاتورة المجانية
      if (Number(finalPrice.toFixed(2)) === 0) {
        // فاتورة هدية
        (async () => {
          try {
            setIsCreatingInvoice(true);
            // جلب تفاصيل أسعار الخدمات للحصول على الحقول (tax, price_before_tax)
            const serviceItems = cart.filter((it) => it.type === "service");
            if (serviceItems.length === 0) {
              setIsCreatingInvoice(false);
              toast.error("فاتورة الهدية مدعومة للخدمات فقط");
              return;
            }

            const details = await Promise.all(
              serviceItems.map(async (it: any) => {
                const priceId = it.price_id || Number(it.id);
                try {
                  const r = await api.get(`/store/service-prices/${priceId}/`);
                  return { id: priceId, data: r.data };
                } catch {
                  return { id: priceId, data: null };
                }
              })
            );

            const order_items = details.map(({ id, data }) => ({
              service: id,
              tax: Number(data?.tax ?? 0),
              price_before_tax: Number(data?.price_before_tax ?? 0),
            }));

            const payloadFree = {
              order_items,
              ...(data.phone && { phone: toE164Ksa(data.phone) }),
            };

            const resp = await api
              .post(`/store/free-invoices/`, payloadFree)
              .then((r: any) => r.data);

            // تجهيز بيانات الطباعة لفاتورة الهدية (بسيطة وفق الطلب)
            const mappedGift = {
              id: resp.id,
              phone: resp.phone || toE164Ksa(data.phone) || "-",
              date:
                resp.created_at?.slice(0, 10) ||
                formatDate(new Date().toISOString()),
              time:
                resp.created_at?.slice(11, 16) ||
                formatTime(new Date().toISOString()),
              items: (resp.order?.items || []).map((it: any) => ({
                id: it.id,
                name:
                  it.service_info?.name || it.subscription_info?.name || "صنف",
                quantity: it.quantity ?? 1,
              })),
            };

            setIsGiftPrint(true);
            setGiftInvoiceData(mappedGift);
            setGeneratedInvoice({
              id: resp.id,
              clientName: "",
              phone: mappedGift.phone,
              discountCode: "",
              totalPrice: 0,
              cashAmount: 0,
              cardAmount: 0,
              date: mappedGift.date,
              time: mappedGift.time,
              items: mappedGift.items.map((x: any) => ({
                id: x.id,
                name: x.name,
                price: "0",
                quantity: x.quantity,
              })),
            } as Invoice);
            setPrintInvoice({
              id: resp.id,
              clientName: "",
              phone: mappedGift.phone,
              discountCode: "",
              totalPrice: 0,
              cashAmount: 0,
              cardAmount: 0,
              date: mappedGift.date,
              time: mappedGift.time,
              items: mappedGift.items.map((x: any) => ({
                id: x.id,
                name: x.name,
                price: "0",
                quantity: x.quantity,
              })),
            } as Invoice);

            toast.success("تم إنشاء فاتورة الهدية بنجاح!");
          } catch (error: any) {
            let backendMessage = "فشل في إنشاء فاتورة الهدية";
            const respData = error?.response?.data;
            if (typeof respData === "string") backendMessage = respData;
            else if (respData && typeof respData === "object") {
              try {
                const parts = Object.entries(respData)
                  .map(
                    ([k, v]) =>
                      `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`
                  )
                  .join(" | ");
                if (parts.trim().length > 0) backendMessage = parts;
              } catch {}
            }
            toast.error(backendMessage);
          } finally {
            setIsCreatingInvoice(false);
          }
        })();
        return;
      }

      const isOrderFromBackend = cart.some((item) => item.order_id);
      let payload;

      if (isOrderFromBackend) {
        // Case 1: Order موجود من الباك اند (من صفحة الطلبات)
        payload = {
          order_id: cart[0].order_id!,
          cash: data.cashAmount,
          visa: data.cardAmount,
          // إرسال visa_order_id فقط إذا كانت قيمة visa أكبر من 0
          ...(data.cardAmount > 0 && {
            visa_order_id:
              pendingPaymentData?.visa_order_id || `ORDER_${cart[0].order_id!}`,
          }),
        };
        // لا نقوم بأي تحقق بعد نجاح الدفع؛ ننشئ الفاتورة مباشرة
      } else {
        // Case 2: فاتورة جديدة من الـ cart (من الـ home)
        const newOrderId =
          pendingPaymentData?.visa_order_id ||
          `ORDER_${generateCryptoUniqueOrderId()}`;

        payload = {
          order_items: cart.map((item) =>
            item.type === "service"
              ? { service: item.price_id || Number(item.id) }
              : { subscription: item.price_id || Number(item.id) }
          ),
          ...(coupon?.code && { coupon: coupon.code }),
          ...(data.phone && { phone: toE164Ksa(data.phone) }),
          cash: data.cashAmount,
          visa: data.cardAmount,
          // إرسال visa_order_id فقط إذا كانت قيمة visa أكبر من 0
          ...(data.cardAmount > 0 && { visa_order_id: newOrderId }),
        };

        // لا نقوم بأي تحقق بعد نجاح الدفع؛ ننشئ الفاتورة مباشرة
      }

      closeConfirmModal();

      // بدء حالة التحميل أثناء إنشاء الفاتورة
      setIsCreatingInvoice(true);
      apiCallInProgress.current = true;

      createInvoice(payload, {
        onSuccess: async (resp: any) => {
          closeConfirmModal();

          try {
            setIsGiftPrint(false);
            // استخدام بيانات الـ response مباشرة بدلاً من جلبها مرة أخرى
            const invoiceData = resp;

            const mapped: Invoice = {
              id: invoiceData.id,
              clientName: invoiceData.order?.client_name || "عميل",
              phone: invoiceData.phone || "-",
              discountCode: invoiceData.coupon_code || "",
              totalPrice: invoiceData.order_total ?? invoiceData.total ?? 0,
              cashAmount: invoiceData.cash ?? 0,
              cardAmount: invoiceData.visa ?? 0,
              date: invoiceData.created_at?.slice(0, 10) || "",
              time: invoiceData.created_at?.slice(11, 16) || "",
              items: (invoiceData.order?.items || []).map((it: any) => ({
                id: it.id,
                name:
                  it.service_info?.name || it.subscription_info?.name || "صنف",
                price: String(it.price ?? 0),
                // حقول تفصيلية لكل عنصر لاستخدامها في الجدول (قبل الضريبة/الضريبة/الشامل)
                price_before_tax: it.price_before_tax ?? undefined,
                priceBeforeTax: it.price_before_tax ?? undefined,
                priceAfterTax: it.price ?? undefined,
                tax: it.tax ?? undefined,
                quantity: it.quantity ?? 1,
              })),
              // تمرير QR من الباك اند للطباعة
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              // @ts-ignore
              qrCode: (invoiceData as any).qr_code,
              // مجاميع من الباك اند
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              // @ts-ignore
              totalBeforeDiscount:
                (invoiceData as any).total_before_discount ??
                (invoiceData as any).order?.total_price?.total_before,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              // @ts-ignore
              totalBeforeTax: (invoiceData as any).total_before_tax,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              // @ts-ignore
              totalTax: (invoiceData as any).total_tax,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              // @ts-ignore
              totalPriceFinal:
                (invoiceData as any).total_price ??
                (invoiceData as any).order?.total_price?.total_after,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              // @ts-ignore
              discountPercentage: (invoiceData as any).discount_percentage,
            };

            setGeneratedInvoice(mapped);
            setPrintInvoice(mapped);
          } catch (error) {
            // إذا فشل في معالجة البيانات، نعرض رسالة خطأ ولا نطبع
            console.error("فشل في معالجة بيانات الفاتورة:", error);
            toast.error("فشل في طباعة الفاتورة لعدم توفر البيانات اللازمة");
            setGeneratedInvoice(null);
            setPrintInvoice(null);
            setIsCreatingInvoice(false);
            return;
          }

          // إشعار بنجاح إنشاء الفاتورة
          toast.success("تم إنشاء الفاتورة بنجاح!");
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: "invoiceResult",
                status: "success",
                response: resp,
              })
            );
          }
          // إنهاء حالة التحميل
          setIsCreatingInvoice(false);
          apiCallInProgress.current = false;
        },
        onError: (error: any) => {
          // ❌ إزالة الفاتورة من الطباعة عند حدوث خطأ
          setPrintInvoice(null);

          // استخراج رسالة الخطأ من الباك اند (إن وُجدت)
          let backendMessage = "فشل في إنشاء الفاتورة، حاول مرة أخرى";
          const respData = error?.response?.data;
          if (typeof respData === "string") {
            backendMessage = respData;
          } else if (respData && typeof respData === "object") {
            try {
              const parts = Object.entries(respData)
                .map(
                  ([k, v]) =>
                    `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`
                )
                .join(" | ");
              if (parts.trim().length > 0) backendMessage = parts;
            } catch {
              // ignore
            }
          }
          toast.error(backendMessage);

          // ✅ Send error to React Native WebView
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: "invoiceError",
                error: {
                  message: error?.message || "Unknown error",
                  response: error?.response?.data || null,
                  status: error?.response?.status || null,
                  requestBody: payload,
                },
              })
            );
          }
          // إنهاء حالة التحميل عند الخطأ
          setIsCreatingInvoice(false);
          apiCallInProgress.current = false;
        },
      });
    },
    [
      cart,
      coupon,
      finalPrice,
      api,
      createInvoice,
      closeConfirmModal,
      pendingPaymentData,
    ]
  );

  const precheckInvoiceBeforeCardPayment = useCallback(
    async (
      data: any,
      forcedVisaOrderId?: string
    ): Promise<
      { ok: true; orderId: string } | { ok: false; error: string }
    > => {
      try {
        if (cart.length === 0) return { ok: false, error: "السلة فارغة" };
        if (!data || Number(data.cardAmount) <= 0)
          return { ok: true, orderId: forcedVisaOrderId || "" };

        // حماية من API call duplicates
        if (apiCallInProgress.current) {
          return { ok: false, error: "API call in progress" };
        }
        apiCallInProgress.current = true;

        const isOrderFromBackend = cart.some((item) => item.order_id);
        let orderIdToUse = forcedVisaOrderId;

        if (!orderIdToUse) {
          // دائماً نولد orderId جديد لضمان عدم التكرار
          orderIdToUse = isOrderFromBackend
            ? `ORDER_${cart[0].order_id!}_${generateCryptoUniqueOrderId()}`
            : `ORDER_${generateCryptoUniqueOrderId()}`;
        }

        if (process.env.NODE_ENV === "development") {
          console.log("🆔 Generated unique orderId:", orderIdToUse);
        }

        // نبني نفس الحمولة المرسلة عند إنشاء الفاتورة تماماً
        const cashAmount = Number(data.cashAmount || 0);
        const cardAmount = Number(data.cardAmount || 0);
        const normalizedPhone = data?.phone
          ? toE164Ksa(data.phone)
          : data?.phoneNumber
          ? toE164Ksa(data.phoneNumber)
          : undefined;

        const checkPayload = isOrderFromBackend
          ? {
              // حالة الطلب القادم من الباك اند
              order_id: cart[0].order_id!,
              cash: cashAmount,
              visa: cardAmount,
              visa_order_id: orderIdToUse,
              // لا نرسل coupon/phone هنا عادةً للطلبات الموجودة، نلتزم بما يرسله الإنشاء
            }
          : {
              // حالة إنشاء فاتورة من السلة
              order_items: cart.map((item) =>
                item.type === "service"
                  ? { service: item.price_id || Number(item.id) }
                  : { subscription: item.price_id || Number(item.id) }
              ),
              ...(coupon?.code && { coupon: coupon.code }),
              ...(normalizedPhone && { phone: normalizedPhone }),
              cash: cashAmount,
              visa: cardAmount,
              visa_order_id: orderIdToUse,
            };

        const checkResp = await api
          .post(
            `/store/invoices/check_invoice_is_correct_or_not/`,
            checkPayload
          )
          .then((r: any) => r.data)
          .catch((err: any) => {
            throw err?.response?.data || err;
          });

        if (!checkResp || checkResp.status === false) {
          const msgParts: string[] = [];
          if (checkResp?.message) msgParts.push(String(checkResp.message));
          if (checkResp?.errors && typeof checkResp.errors === "object") {
            try {
              const details = Object.entries(checkResp.errors)
                .map(
                  ([k, v]) =>
                    `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`
                )
                .join(" | ");
              if (details) msgParts.push(details);
            } catch {}
          }

          const finalMsg = msgParts.join("\n") || "الفاتورة غير صالحة";
          toast.error(finalMsg);
          showErrorPopup?.(finalMsg);
          return { ok: false, error: finalMsg };
        }

        apiCallInProgress.current = false;
        return { ok: true, orderId: orderIdToUse };
      } catch (e: any) {
        apiCallInProgress.current = false;
        const respData = e;
        let finalMsg = "فشل التحقق من صحة الفاتورة";
        if (respData?.message) finalMsg = respData.message;
        if (respData?.errors && typeof respData.errors === "object") {
          try {
            const details = Object.entries(respData.errors)
              .map(
                ([k, v]) =>
                  `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`
              )
              .join(" | ");
            if (details) finalMsg = `${finalMsg}\n${details}`;
          } catch {}
        }
        toast.error(finalMsg);
        showErrorPopup?.(finalMsg);
        return { ok: false, error: finalMsg };
      }
    },
    [api, cart, showErrorPopup]
  );

  return {
    onSubmit,
    handlePrint,
    generatedInvoice,
    printInvoice,
    setPrintInvoice,
    setGeneratedInvoice,
    isCreatingInvoice,
    isGiftPrint,
    giftInvoiceData,
    precheckInvoiceBeforeCardPayment,
  };
};
